// MYS-7340: configurable "Quick Menu" call-to-action buttons on Service Providers and Products.
// Stored inline on the MYS entry (like product infos) and exported to Content Hub as
// "callToActionButton" entries linked from the parent's `callToActionButton` multi-reference.

type LocaleCode = 'de' | 'en' | 'fr' | 'it' | 'nl'
type I18n = Partial<Record<LocaleCode, string>>

export const MAX_CTA_BUTTONS = 5
export const CTA_LABEL_MAX_LENGTH = 50 // Content Hub validation on callToActionButton.actionText
export const CTA_FILE_CONTENT_TYPE = 'application/pdf' // downloads are PDFs only
export const CTA_FILE_MAX_SIZE = 10 * 1024 * 1024

export enum CtaActionType {
	EXTERNAL_LINK = 'externalLink',
	PHONE = 'phone',
	EMAIL = 'email',
	DOWNLOAD = 'download',
}

export type CtaAsset = {
	id: string // MYS asset id
	fileName?: string
	size?: number // bytes, for display only
}

export type CtaButton = {
	id: string
	label: I18n
	actionType: CtaActionType
	// externalLink / phone / email: per locale, empty locales fall back to the default locale
	value?: I18n
	// download: uploaded file per locale
	assets?: Partial<Record<LocaleCode, CtaAsset>>
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const PHONE_PATTERN = /^\+?[0-9 ()/.-]{3,}$/
const URL_PATTERN = /^https?:\/\/\S+$/i

export function firstNonEmpty(value: Record<string, string> | undefined, preferred: string[] = []): string | undefined {
	if (!value) return undefined
	for (const code of preferred) {
		if (code && value[code]?.trim()) return value[code].trim()
	}
	return Object.values(value).find(v => typeof v === 'string' && v.trim())?.trim()
}

/** MYS asset id per locale of a download button */
export function ctaAssetIds(cta: CtaButton): Record<string, string> {
	const ids: Record<string, string> = {}
	for (const [code, asset] of Object.entries(cta.assets ?? {})) {
		if (asset?.id) ids[code] = asset.id
	}
	return ids
}

export function isValidCtaValue(actionType: CtaActionType, value: string | undefined): boolean {
	if (!value) return false
	switch (actionType) {
		case CtaActionType.EXTERNAL_LINK: return URL_PATTERN.test(value)
		case CtaActionType.EMAIL: return EMAIL_PATTERN.test(value)
		case CtaActionType.PHONE: return PHONE_PATTERN.test(value)
		default: return false
	}
}

/**
 * Returns a list of human readable problems, empty when the buttons are valid.
 */
export function validateCtaButtons(ctaButtons: CtaButton[] | undefined): string[] {
	const errors: string[] = []
	if (!ctaButtons?.length) return errors

	if (ctaButtons.length > MAX_CTA_BUTTONS) {
		errors.push(`At most ${MAX_CTA_BUTTONS} call to action buttons are allowed`)
	}

	ctaButtons.forEach((cta, index) => {
		const position = `Call to action button ${index + 1}`
		if (!firstNonEmpty(cta.label as Record<string, string>)) {
			errors.push(`${position}: label is required`)
		}
		for (const label of Object.values(cta.label ?? {})) {
			if (typeof label === 'string' && label.length > CTA_LABEL_MAX_LENGTH) {
				errors.push(`${position}: label must not exceed ${CTA_LABEL_MAX_LENGTH} characters`)
				break
			}
		}
		if (!Object.values(CtaActionType).includes(cta.actionType)) {
			errors.push(`${position}: action type is required`)
			return
		}
		if (cta.actionType === CtaActionType.DOWNLOAD) {
			if (!firstNonEmpty(ctaAssetIds(cta))) {
				errors.push(`${position}: file is required`)
			}
			return
		}
		const values = Object.values(cta.value ?? {}).filter(v => typeof v === 'string' && v.trim())
		if (!values.length || !values.every(v => isValidCtaValue(cta.actionType, v.trim()))) {
			errors.push(`${position}: invalid ${cta.actionType}`)
		}
	})

	return errors
}

/**
 * Drops properties that do not belong to the chosen action type, so switching the type
 * in the UI does not leave stale data behind.
 */
export function normalizeCtaButton(cta: CtaButton): CtaButton {
	const normalized: CtaButton = {
		id: cta.id,
		label: cta.label ?? {},
		actionType: cta.actionType,
	}
	if (cta.actionType === CtaActionType.DOWNLOAD) {
		normalized.assets = {}
		for (const [code, asset] of Object.entries(cta.assets ?? {})) {
			if (asset?.id) normalized.assets[code] = { id: asset.id, fileName: asset.fileName, size: asset.size }
		}
	}
	else {
		normalized.value = cta.value ?? {}
	}
	return normalized
}
